import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { getCachedResult } from '@yme/api';
import { Alert, AlertType } from '@yme/atoms/alert';
import { MobileDropdownSlider } from '@yme/atoms/mobile-dropdown-slider';
import { ModalWindowFooter } from '@yme/atoms/modal-footer';
import { LG_MODAL, MD_MODAL, StaticModalWindow } from '@yme/atoms/modal-window';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { Switcher } from '@yme/atoms/switcher';
import { PRODUCTS_ATTRIBUTE, SELECT_MODEL_MODAL_CASHED_DATA } from '@yme/common/constants';
import { getRootPath } from '@yme/components/CheckoutForm/PaymentSteps/selectors';
import { COOKIE_HYPERDRIVE_SELECTED_UNIT, COOKIE_COOKIE_HYPERDRIVE_SELECTED_UNIT_EXPIRE, } from '@yme/components/ProductDetailsPage/components/ProductCard/CardAccessories/constants';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { setIsModalHide } from '@yme/modals/actions';
import { getModalData } from '@yme/modals/selectors';
import { getRTL } from '@yme/selectors/common';
import { getAttribute } from '@yme/selectors/products';
import { colorAccentError900, colorAccentSuccess800 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel4 } from '@yme/tokens/typography';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { ACCESSORIES_COMPATIBILITY_KEY } from '@yme/utils/constants';
import { SearchByName } from './SearchByName';
import { SearchByType } from './SearchByType';
import { HIDE_MODAL_ANIMATION_TIME, SWITCHER_NAME, SWITCHER_OPTIONS, SelectModelModalType, } from './constants';
import { checkCompatibility, getModelName, replaceModelNameWithJSX } from './helpers';
import { getCategoriesOfProduct, getUnitProductTypeId } from './services';
export function SelectYourModelModal() {
    const [productTypeId, setProductTypeId] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [categoriesValues, setCategoriesValues] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTypeOptionDisabled, setIsTypeOptionDisabled] = useState(false);
    const [isMobileModalActive, setIsMobileModalActive] = useState(false);
    const isRightToLeft = useSelector(getRTL);
    const languageRootPath = useSelector(getRootPath);
    const { isInfiniteDesktopView, isTabletView } = useWindowSize();
    const { type = SelectModelModalType.DEFAULT, title, primaryButtonContent, secondaryButtonContent, labels, callback, isAnalyticTracked, endpoints, defaultStartYear, categories, segments = [], isPDP, } = useSelector(getModalData);
    const isSegmentsExist = Boolean(segments.length && endpoints?.segmentsInfo);
    const suitableProductsKeys = useSelector(getAttribute(PRODUCTS_ATTRIBUTE));
    const isSuccessCompatibilityCheck = useMemo(() => {
        if (!selectedProduct) {
            return false;
        }
        return checkCompatibility({
            key: selectedProduct?.key,
            suitableProductsKeys,
        });
    }, [selectedProduct, suitableProductsKeys]);
    const isCompatibilityAlertShowed = useMemo(() => Boolean(isPDP && selectedProduct), [isPDP, selectedProduct]);
    const isComparisonModal = type === SelectModelModalType.COMPARISON;
    const [currentValue, setCurrentValue] = useState(isComparisonModal ? SWITCHER_OPTIONS.OPTION_BY_TYPE : SWITCHER_OPTIONS.OPTION_BY_NAME);
    const dispatch = useDispatch();
    const isTracked = useRef(false);
    useEffect(() => {
        if (isComparisonModal || isTracked.current) {
            return;
        }
        isTracked.current = true;
        setDigitalData(DigitalDataMutation.COMPATIBILITY_FORM_ENTRY, null);
    }, [isComparisonModal]);
    const handleHideModal = useCallback(() => {
        dispatch(setIsModalHide());
    }, [dispatch]);
    const closeMobileModal = useCallback(() => {
        setIsMobileModalActive(false);
        setTimeout(() => {
            handleHideModal();
        }, HIDE_MODAL_ANIMATION_TIME);
    }, [handleHideModal]);
    const onSubmitHandleModal = useCallback(() => {
        if (callback && selectedProduct && !isComparisonModal) {
            localStorage.setItem(ACCESSORIES_COMPATIBILITY_KEY, JSON.stringify(selectedProduct));
            Cookies.set(COOKIE_HYPERDRIVE_SELECTED_UNIT, selectedProduct.key, {
                expires: COOKIE_COOKIE_HYPERDRIVE_SELECTED_UNIT_EXPIRE,
                path: `${languageRootPath}/`,
            });
            callback(selectedProduct);
            if (isAnalyticTracked) {
                setDigitalData(DigitalDataMutation.ACC_SELECT_MODEL_FILTER, {
                    searchType: currentValue,
                    product: selectedProduct,
                });
            }
        }
        if (callback && isComparisonModal) {
            callback(selectedProduct);
        }
        if (isInfiniteDesktopView) {
            handleHideModal();
        }
        else {
            closeMobileModal();
        }
    }, [
        callback,
        closeMobileModal,
        currentValue,
        handleHideModal,
        isAnalyticTracked,
        isComparisonModal,
        isInfiniteDesktopView,
        languageRootPath,
        selectedProduct,
    ]);
    useEffect(() => {
        let shouldUpdate = true;
        if (shouldUpdate) {
            getUnitProductTypeId(endpoints.projectInfo).then((productTypeIdValue) => {
                setProductTypeId(productTypeIdValue);
            });
            getCategoriesOfProduct(isSegmentsExist ? endpoints.segmentsInfo : endpoints.categoryInfo)
                .then((categoriesData) => {
                setCategoriesValues(categoriesData);
            })
                .catch(() => setIsTypeOptionDisabled(true));
        }
        return () => {
            shouldUpdate = false;
        };
    }, [endpoints, isSegmentsExist]);
    useLayoutEffect(() => {
        let timeout;
        if (!isInfiniteDesktopView) {
            timeout = setTimeout(() => {
                setIsMobileModalActive(true);
            }, 0);
        }
        return () => clearTimeout(timeout);
    }, [isInfiniteDesktopView]);
    const switcherProps = [
        {
            id: SWITCHER_OPTIONS.OPTION_BY_NAME,
            label: get(labels, 'searchNameLabel'),
            value: SWITCHER_OPTIONS.OPTION_BY_NAME,
            isChecked: true,
            isDisabled: false,
        },
        {
            id: SWITCHER_OPTIONS.OPTION_BY_TYPE,
            label: get(labels, 'searchTypeLabel'),
            value: SWITCHER_OPTIONS.OPTION_BY_TYPE,
            isChecked: false,
            isDisabled: isTypeOptionDisabled,
        },
    ];
    const checkedCurrentValue = useCallback((value) => {
        setCurrentValue(value);
        setSelectedProduct(null);
    }, []);
    const getSwitcherOptionForm = (value) => {
        const cashedModels = getCachedResult(SELECT_MODEL_MODAL_CASHED_DATA);
        const isSearchByTypeShowed = Boolean(productTypeId || cashedModels?.length);
        const searchByName = (React.createElement(SearchByName, { resultsListRangeOfMaxVisibleItems: 4, setSelectedProduct: setSelectedProduct, labels: labels, productTypeId: productTypeId, productsByQueryEnd: endpoints.productsByQuery, isRightToLeft: isRightToLeft }));
        switch (value) {
            case SWITCHER_OPTIONS.OPTION_BY_NAME:
                return searchByName;
            case SWITCHER_OPTIONS.OPTION_BY_TYPE:
                return isSearchByTypeShowed ? (React.createElement(SearchByType, { isComparisonModal: isComparisonModal, setSelectedProduct: setSelectedProduct, labels: labels, defaultStartYear: defaultStartYear, productsByQueryEnd: endpoints.productsByQuery, comparingProductsByQuery: endpoints?.comparingProductsByQuery, categories: isSegmentsExist ? segments : categories, categoriesValues: categoriesValues, isSegmentsExist: isSegmentsExist, productTypeId: productTypeId, setIsLoading: setIsLoading, segmentsInfoEndpoint: endpoints.segmentsInfo, isRightToLeft: isRightToLeft })) : null;
            default:
                return searchByName;
        }
    };
    const modalContent = (React.createElement(FlexWrapper, { isFullWidth: true, flexDirection: "column", gap: "24px" },
        !isComparisonModal && (React.createElement(Switcher, { checkedCurrentValue: checkedCurrentValue, switcherArguments: switcherProps, isRightToLeft: isRightToLeft, name: SWITCHER_NAME })),
        getSwitcherOptionForm(currentValue),
        isCompatibilityAlertShowed && (React.createElement(Alert, { isFullWidth: true, hideExtraContent: true, type: isSuccessCompatibilityCheck ? AlertType.SUCCESS : AlertType.ERROR, message: React.createElement("span", null, replaceModelNameWithJSX({
                text: (isSuccessCompatibilityCheck
                    ? labels?.accessoryIsCompatible
                    : labels?.accessoryNotCompatible) || '',
                jsxFragment: (React.createElement(RegularTextLevel4, { color: isSuccessCompatibilityCheck ? colorAccentSuccess800 : colorAccentError900, weight: 500 }, getModelName(selectedProduct))),
            })) }))));
    return (React.createElement(React.Fragment, null,
        React.createElement(SpinnerLoader, { isLoading: isLoading, fullWidth: true }),
        isInfiniteDesktopView ? (React.createElement(StaticModalWindow, { onHide: handleHideModal, minHeight: "auto", staticModalSize: isComparisonModal ? MD_MODAL : LG_MODAL, title: title, isRightToLeft: isRightToLeft, footerComponent: React.createElement(ModalWindowFooter, { isCancelButton: Boolean(secondaryButtonContent), isSubmitButton: true, submitButtonLabel: primaryButtonContent, cancelButtonLabel: secondaryButtonContent, onSubmit: onSubmitHandleModal, onCancel: handleHideModal, isSubmitButtonDisabled: Boolean(!selectedProduct) }) }, modalContent)) : (React.createElement(MobileDropdownSlider, { isActive: isMobileModalActive, handleActiveMobileSlider: closeMobileModal, title: title, isRightToLeft: isRightToLeft, footerComponent: React.createElement(ModalWindowFooter, { isCancelButton: Boolean(secondaryButtonContent), isSubmitButton: true, cancelButtonCustomPadding: isTabletView ? '8px 96px' : undefined, submitButtonLabel: primaryButtonContent, cancelButtonLabel: secondaryButtonContent, isSubmitButtonFullWidth: true, onSubmit: onSubmitHandleModal, onCancel: closeMobileModal, isSubmitButtonDisabled: Boolean(!selectedProduct) }) }, modalContent))));
}
